import { useNavigation } from '../app/navigation';
import { GlobalStatus } from '../components/features/GlobalStatus';
import { StatusNudge } from '../components/features/StatusNudge';
import { TransactionList } from '../components/features/TransactionList';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { creditIconFor } from '../components/ui/currency';
import { Panel } from '../components/ui/Panel';
import { Mascot } from '../components/mascot/Mascot';
import { StatTile } from '../components/ui/StatTile';
import { creditWord, formatCredits } from '../lib/format';
import { useGame } from '../game/store';
import type { Transaction } from '../types';
import { useSession } from '../state/session';
import layout from '../styles/layout.module.css';
import styles from './HomePage.module.css';

/**
 * Home answers three questions, in this order, above everything else: what is
 * my status and how far is the next one, where do I stand across the whole
 * server, and where do I spin. Everything below that is reference material.
 *
 * The order is the design. What used to sit at the top was a welcome banner and
 * four stat tiles — true, and none of it a reason to do anything.
 */
export function HomePage() {
  const { user, theme, profiles } = useSession();
  const creditIcon = creditIconFor(theme);
  const { balance, transactions, spins, coupons, wheel, naming } = useGame();
  const { navigate } = useNavigation();

  const recent: Transaction[] = transactions.slice(0, 5).map((t) => ({
    id: t.id,
    label: t.reason,
    kind: t.type === 'grant' || t.type === 'reward' ? 'earn' : 'spend',
    amount: t.amount,
    at: t.at,
  }));

  // A spin carries a status only when it won something a human has to hand
  // over, so this is the whole of "do I have a prize waiting".
  const waiting = spins.filter(
    (spin) => spin.status === 'pending' || spin.status === 'accepted',
  ).length;
  const usableCoupons = coupons.filter((coupon) => !coupon.usedAt).length;

  return (
    <>
      {/* The server-wide card only earns its place next to more than one realm; with
          a single one it would repeat the status beside it. */}
      <div className={profiles.length > 1 ? styles.top : styles.topSolo}>
        <StatusNudge onOpenProfile={() => navigate('profile')} />
        {profiles.length > 1 && <GlobalStatus />}
      </div>

      <section className={styles.play}>
        <div className={styles.playMascot}>
          <Mascot size={112} />
        </div>
        <div className={styles.playBody}>
          <span className={styles.eyebrow}>
            <Icon name="bolt" size={13} />
            Ready when you are, {user?.displayName ?? 'player'}
          </span>
          <h2 className={styles.playTitle}>Spin a drop</h2>
        </div>
        <div className={styles.playActions}>
          <Button variant="primary" size="lg" icon="wheel" onClick={() => navigate('wheel')}>
            {wheel('main').name || 'Lucky Drop'}
          </Button>
          <Button size="lg" icon="crown" onClick={() => navigate('daily')}>
            {wheel('daily').name || 'Premium Drop'}
          </Button>
          {/* Only for realms that have switched the card on. */}
          {wheel('scratch').enabled && (
            <Button size="lg" icon="ticket" onClick={() => navigate('scratch')}>
              {wheel('scratch').name || 'Scratch Card'}
            </Button>
          )}
        </div>
      </section>

      <div className={layout.grid}>
        <StatTile
          icon={creditIcon}
          accent="gold"
          label={naming.creditNamePlural}
          value={formatCredits(balance)}
          hint="Available balance"
        />
        <StatTile
          icon="gift"
          accent="brand"
          label="Rewards waiting"
          value={`${waiting}`}
          hint={waiting ? 'The team is on it' : 'Nothing pending'}
        />
        <StatTile
          icon="ticket"
          accent="mint"
          label="Coupons"
          value={`${usableCoupons}`}
          hint="Stackable, single use"
        />
      </div>

      <div className={layout.split}>
        <Panel
          title="Recent activity"
          actions={
            <Badge tone={recent.length ? 'brand' : 'neutral'}>
              {recent.length ? 'Live' : 'Empty'}
            </Badge>
          }
          subtitle={`Your latest ${creditWord(naming)} movements`}
        >
          {recent.length ? (
            <TransactionList transactions={recent} />
          ) : (
            <p className={layout.sectionHint}>No activity yet — spin a drop to get started.</p>
          )}
        </Panel>

        <Panel title="Where to next" subtitle="The three you'll use most">
          <div className={layout.stackTight}>
            <Button icon={creditIcon} fullWidth onClick={() => navigate('shop')}>
              Earn {creditWord(naming)}
            </Button>
            <Button icon="gift" fullWidth onClick={() => navigate('rewards')}>
              My rewards
            </Button>
            <Button icon="bolt" fullWidth onClick={() => navigate('howto')}>
              How to play
            </Button>
          </div>
        </Panel>
      </div>
    </>
  );
}
